import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useApiContext } from '@/contexts/apiContext';
import { EntityType } from '../entities/entityType';
import ErrorMessage from '../errorMessage';
import StorageIcon from '@mui/icons-material/Storage';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import WidgetsIcon from '@mui/icons-material/Widgets';

type Props = {
  entityType: EntityType;
  open: boolean;
  onCancel: () => void;
  onSubmit: (entityDefId: string, entityInstanceId?: string) => void;
};

const AddEntityDialog = ({ entityType, open, onCancel, onSubmit }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [entityDefs, setEntityDefs] = useState<any[]>([]);
  const [entityInstances, setEntityInstanes] = useState<any[]>([]);
  const [entityDefId, setEntityDefId] = useState<string>('');
  const [entityInstanceId, setEntityInstanceId] = useState<string>('');
  const [createType, setCreateType] = useState<string>('new');

  const [errors, setErrors] = useState({} as any);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const loadUserAppDefs = async () => {
      try {
        const response = await apiContext.getUserAppDefs();
        await setEntityDefs(response);
      } catch (error) {
        setError(error);
      }
    };
    const loadDataSourceDefs = async () => {
      try {
        const response = await apiContext.getDataSourceDefs();
        await setEntityDefs(response);
      } catch (error) {
        setError(error);
      }
    };
    const loadAgentDefs = async () => {
      try {
        const response = await apiContext.getAgentDefs();
        await setEntityDefs(response);
      } catch (error) {
        setError(error);
      }
    };

    if (entityType === EntityType.dataSourceInstance) {
      loadDataSourceDefs();
    } else if (entityType === EntityType.agentInstance) {
      loadAgentDefs();
    } else if (entityType === EntityType.userAppInstance) {
      loadUserAppDefs();
    }
  }, [entityType]);

  useEffect(() => {
    const loadUserAppInstances = async () => {
      try {
        const response = await apiContext.getUserAppInstancesOfType(entityDefId);
        await setEntityInstanes(response);
      } catch (error) {
        setError(error);
      }
    };
    const loadDataSourceInstances = async () => {
      try {
        const response = await apiContext.getDataSourceInstancesOfType(entityDefId);
        await setEntityInstanes(response);
      } catch (error) {
        setError(error);
      }
    };
    const loadAgentInstances = async () => {
      try {
        const response = await apiContext.getAgentInstancesOfType(entityDefId);
        await setEntityInstanes(response);
      } catch (error) {
        setError(error);
      }
    };

    if (entityType && entityDefId && createType === 'existing') {
      if (entityType === EntityType.dataSourceInstance) {
        loadDataSourceInstances();
      } else if (entityType === EntityType.agentInstance) {
        loadAgentInstances();
      } else if (entityType === EntityType.userAppInstance) {
        loadUserAppInstances();
      }
    }
  }, [entityType, entityDefId, createType]);

  const addEntity = async () => {
    setError(null);
    const errors = { entityDefId: '', entityInstanceId: '' };
    let hasError = false;
    if (!entityDefId) {
      errors['entityDefId'] = 'Required';
      hasError = true;
    }
    if (createType === 'existing' && !entityInstanceId) {
      errors['entityInstanceId'] = 'Required';
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      onSubmit(entityDefId, createType === 'existing' ? entityInstanceId : undefined);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{`${t('common.buttons.add')} ${t(`common.entityType.${entityType}`)}`}</DialogTitle>
      <Divider />
      <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 }, pt: 2 }}>
        <Stack spacing={3}>
          {error && <ErrorMessage error={error} />}

          <Autocomplete
            options={entityDefs}
            getOptionLabel={(option) => option.name || ''}
            value={entityDefs.find((ed) => ed.id === entityDefId) || null}
            onChange={(_, newValue) => {
              setEntityDefId(newValue?.id || '');
              setEntityInstanceId('');
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                  {entityType === EntityType.dataSourceInstance ? (
                    <StorageIcon fontSize="small" />
                  ) : entityType === EntityType.agentInstance ? (
                    <SplitscreenIcon fontSize="small" />
                  ) : (
                    <WidgetsIcon fontSize="small" />
                  )}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name}
                  </Typography>
                  {option.implementationStack && (
                    <Typography variant="caption" color="text.secondary">
                      {option.implementationStack}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t(`common.entityType.${entityType}`)} Type`}
                required
                error={!!errors.entityDefId}
                helperText={errors.entityDefId}
                placeholder="Search for a type..."
              />
            )}
          />

          <RadioGroup
            value={createType}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCreateType((event.target as HTMLInputElement).value);
            }}
            row
            sx={{ justifyContent: 'center' }}
          >
            <FormControlLabel
              value={'new'}
              control={<Radio size="small" />}
              label={'Create New'}
              disabled={!entityDefId}
            />
            <FormControlLabel
              value={'existing'}
              control={<Radio size="small" />}
              label={'Use Existing'}
              disabled={!entityDefId}
            />
          </RadioGroup>

          {createType === 'existing' && entityDefId && (
            <Autocomplete
              options={entityInstances}
              getOptionLabel={(option) => option.name || ''}
              value={entityInstances.find((ei) => ei.id === entityInstanceId) || null}
              onChange={(_, newValue) => setEntityInstanceId(newValue?.id || '')}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
                  <Avatar sx={{ bgcolor: 'secondary.light', width: 32, height: 32 }}>
                    {entityType === EntityType.dataSourceInstance ? (
                      <StorageIcon fontSize="small" />
                    ) : entityType === EntityType.agentInstance ? (
                      <SplitscreenIcon fontSize="small" />
                    ) : (
                      <WidgetsIcon fontSize="small" />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t(`common.entityType.${entityType}`)} Instance`}
                  required
                  error={!!errors.entityInstanceId}
                  helperText={errors.entityInstanceId}
                  placeholder="Search for an instance..."
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onCancel}>
          {t('common.buttons.cancel')}
        </Button>
        <Button size="small" color="primary" variant="contained" onClick={addEntity}>
          {t('common.buttons.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntityDialog;
